import { API_BASE } from '../constants/api';

export function getAuthToken(): string | null {
  return localStorage.getItem('auth_token');
}

export function getAuthHeaders(token?: string | null): HeadersInit {
  const authToken = token ?? getAuthToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  return headers;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public errors?: Record<string, string[]>,
    public reason?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const token = getAuthToken();
  const headers = new Headers(options.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const reason =
      typeof payload.reason === 'string'
        ? payload.reason
        : Array.isArray(payload.errors?.reason)
          ? payload.errors.reason[0]
          : undefined;
    throw new ApiError(
      payload.message || `Request failed (${response.status})`,
      response.status,
      payload.errors,
      reason,
    );
  }

  return (payload.data ?? payload) as T;
}

export async function apiMutation<T>(
  path: string,
  body?: unknown,
  method = 'POST',
): Promise<T> {
  return apiRequest<T>(path, {
    method,
    body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  });
}
